/**
 * RestrictedFeatureCTADialog
 *
 * Modal dialog shown when users attempt to access features that require
 * the desktop app in browser mode. Provides clear messaging and download CTA.
 */

import { Download, Monitor, Database, Terminal as TerminalIcon, HardDrive, FileText, FolderPlus } from 'lucide-react';
import { PRODUCT_NAME } from '../browserModeUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export type RestrictedFeature =
  | 'terminal'
  | 'fileOpen'
  | 'fileSave'
  | 'mssql'
  | 'pgsql'
  | 'backup'
  | 'mountFolder';

interface RestrictedFeatureCTADialogProps {
  open: boolean;
  onClose: () => void;
  feature: RestrictedFeature;
}

const featureConfig: Record<
  RestrictedFeature,
  {
    title: string;
    description: string;
    icon: typeof TerminalIcon;
    benefits: string[];
  }
> = {
  terminal: {
    title: 'Terminal requires Desktop App',
    description:
      `The integrated terminal with AI assistance is only available in the desktop version of ${PRODUCT_NAME}.`,
    icon: TerminalIcon,
    benefits: [
      'AI-powered terminal with database context',
      'Run shell commands alongside your queries',
      'Full terminal emulation with Zsh/Bash',
    ],
  },
  fileOpen: {
    title: 'Open Files requires Desktop App',
    description:
      `Opening SQL files from your local file system requires the desktop version of ${PRODUCT_NAME}.`,
    icon: FileText,
    benefits: [
      'Open any .sql file from your computer',
      'Save queries to your local file system',
      'Full file system integration',
    ],
  },
  fileSave: {
    title: 'Save Files requires Desktop App',
    description:
      `Saving files to your local file system requires the desktop version of ${PRODUCT_NAME}.`,
    icon: HardDrive,
    benefits: [
      'Save queries anywhere on your computer',
      'Organize your SQL files in folders',
      'Quick access to recent files',
    ],
  },
  mssql: {
    title: 'SQL Server requires Desktop App',
    description:
      `Connecting to Microsoft SQL Server databases requires the desktop version of ${PRODUCT_NAME}.`,
    icon: Database,
    benefits: [
      'Connect to SQL Server, Azure SQL, and more',
      'Full IntelliSense and code completion',
      'Execute stored procedures and manage schemas',
    ],
  },
  pgsql: {
    title: 'PostgreSQL requires Desktop App',
    description:
      `Connecting to PostgreSQL databases requires the desktop version of ${PRODUCT_NAME}.`,
    icon: Database,
    benefits: [
      'Connect to PostgreSQL servers',
      'Full IntelliSense support',
      'Manage schemas, tables, and functions',
    ],
  },
  backup: {
    title: 'Backup & Restore requires Desktop App',
    description:
      `Database backup and restore operations require the desktop version of ${PRODUCT_NAME}.`,
    icon: HardDrive,
    benefits: [
      'Backup databases to local files',
      'Restore from backup files',
      'Automated backup scheduling',
    ],
  },
  mountFolder: {
    title: 'Mount Folder requires Desktop App',
    description:
      `Mounting local folders to browse SQL scripts requires the desktop version of ${PRODUCT_NAME}.`,
    icon: FolderPlus,
    benefits: [
      'Mount project folders to browse .sql files',
      'Organize and search your SQL scripts',
      'Quick access to your local SQL files',
    ],
  },
};

export function RestrictedFeatureCTADialog({
  open,
  onClose,
  feature,
}: RestrictedFeatureCTADialogProps) {
  const config = featureConfig[feature];
  const Icon = config.icon;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="restricted-feature-cta-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
            <DialogTitle>{config.title}</DialogTitle>
          </div>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Benefits list */}
          <div className="space-y-2">
            <p className="text-sm font-medium">With the desktop app, you can:</p>
            <ul className="space-y-1.5">
              {config.benefits.map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                >
                  <Monitor className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Browser mode info */}
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
            <p>
              Browser mode supports SQLite databases only. Download the desktop
              app for full SQL Server, PostgreSQL, and MySQL/MariaDB support.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button asChild className="w-full" data-testid="download-desktop-cta">
            <a href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=restricted-feature-dialog#download" target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4 mr-2" />
              Download {PRODUCT_NAME}
            </a>
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={onClose}
            data-testid="continue-demo-button"
          >
            Continue with Demo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
